import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";

import type { Stop } from "~/features/ftl/shared/ftl-stop";

import { hasValidStopDetails } from "../stop-details-validator/stop-details-validator.ts";
import styles from "./stop-navigation-panel.module.css";

export const activeStopStyle =
	"k-text-app-surface k-border-solid k-border-on-primary k-border-2 k-px-2 k-py-0.5";
export const primaryStopStyle =
	"k-text-primary k-border-solid k-border-primary k-border-2 k-px-2 k-py-0.5";
export const errorStopStyle =
	"k-text-error k-border-solid k-border-error k-border-2 k-px-2 k-py-0.5";
export const successStopStyle =
	"k-text-success k-border-solid k-border-success k-border-2 k-px-2 k-py-0.5";

type StopNavigationPanelProps = {
	stops: Stop[];
	activeStopIndex: number;
	onStopSelect: (index: number) => void;
	formRenderProps: FormRenderProps;
	lastSubmitTime: number;
};

export function StopNavigationPanel({
	stops,
	activeStopIndex,
	onStopSelect,
	formRenderProps,
	lastSubmitTime,
}: Readonly<StopNavigationPanelProps> & { formRenderProps: FormRenderProps }) {
	// Count pickups and deliveries separately for display
	const getStopLabel = (stop: Stop, currentIndex: number): string => {
		if (!stop.stopType) return "";

		const stopTypeCount = stops
			.slice(0, currentIndex + 1)
			.filter((s) => s.stopType === stop.stopType).length;

		return `${stop.stopType} ${stopTypeCount}`;
	};

	const getStopBadgeClass = (currentIndex: number) => {
		if (hasValidStopDetails(formRenderProps, currentIndex)) {
			return successStopStyle;
		}
		if (lastSubmitTime > 0) {
			return errorStopStyle;
		}
		return primaryStopStyle;
	};

	return (
		<div
			aria-label={"Stop navigation"}
			className={styles.navigationContainer}
			role={"list"}
		>
			{stops.map((stop, index) => {
				const isActive = index === activeStopIndex;
				return (
					<button
						key={stop.id}
						className={`${styles.stopItem} ${isActive ? styles.activeStop : ""}`}
						onClick={() => onStopSelect(index)}
						type={"button"}
					>
						<div className={styles.stopItemContent}>
							<span
								className={[
									styles.stopBadge,
									isActive ? activeStopStyle : getStopBadgeClass(index),
								].join(" ")}
							>
								{getStopLabel(stop, index)}
							</span>
							<span
								className={`${isActive ? styles.activeLocationText : styles.locationText} `}
							>
								{stop.location?.postalCode ? (
									<span className={"k-font-bold"}>
										{stop.location.postalCode} - {stop.location.city},{" "}
										{stop.location.state}
									</span>
								) : null}
							</span>
						</div>
					</button>
				);
			})}
		</div>
	);
}
